import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { BaseFormModal } from '@/components/BaseFormModal';
import { Dropdownlist } from '@/components/Dropdownlist';
import { DateTimePicker } from '@/components/DateTimePicker';
import { focusAfterPaint } from '@/utils/dom';

export interface AlertType {
  id: number;
  type: string;
}

export interface CallAlertData {
  id?: number;
  locFormattedAddress?: string;
  callAlertTypeId?: number | null;
  message?: string;
  alertStartTime?: string | Date | null;
  alertEndTime?: string | Date | null;
}

export interface AddEditCallAlertSaveData {
  id?: number;
  locFormattedAddress: string;
  callAlertTypeId: number;
  message: string;
  alertStartTime: Date | null;
  alertEndTime: Date | null;
  originalLocation: string;
  locationChanged: boolean;
}

export interface AddEditCallAlertModalProps {
  open: boolean;
  mode?: 'add' | 'edit';
  callAlert?: CallAlertData;
  alertTypes?: AlertType[];
  theme?: string;
  saving?: boolean;
  onSave: (data: AddEditCallAlertSaveData) => void;
  onCancel: () => void;
}

export function AddEditCallAlertModal({
  open,
  mode = 'add',
  callAlert = {},
  alertTypes = [],
  theme,
  saving = false,
  onSave,
  onCancel,
}: AddEditCallAlertModalProps) {
  const [locFormattedAddress, setLocFormattedAddress] = useState('');
  const [callAlertTypeId, setCallAlertTypeId] = useState<number | null>(null);
  const [message, setMessage] = useState('');
  const [alertStartTime, setAlertStartTime] = useState<Date | null>(null);
  const [alertEndTime, setAlertEndTime] = useState<Date | null>(null);
  const [originalLocation, setOriginalLocation] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  // Populate/reset form when modal opens
  useEffect(() => {
    if (open) {
      if (mode === 'edit' && callAlert) {
        setLocFormattedAddress(callAlert.locFormattedAddress || '');
        setOriginalLocation(callAlert.locFormattedAddress || '');
        setCallAlertTypeId(callAlert.callAlertTypeId ?? null);
        setMessage(callAlert.message || '');
        setAlertStartTime(
          callAlert.alertStartTime ? new Date(callAlert.alertStartTime) : null
        );
        setAlertEndTime(
          callAlert.alertEndTime ? new Date(callAlert.alertEndTime) : null
        );
      } else {
        setLocFormattedAddress('');
        setOriginalLocation('');
        setCallAlertTypeId(null);
        setMessage('');
        setAlertStartTime(null);
        setAlertEndTime(null);
      }

      focusAfterPaint(inputRef);
    }
  }, [open, mode, callAlert]);

  const modalTitle = mode === 'edit' ? 'Edit Call Alert' : 'Add Call Alert';
  const saveButtonText = mode === 'edit' ? 'Update' : 'Save';

  const alertTypeItems = useMemo(
    () =>
      (alertTypes || []).map((t) => ({
        label: t.type,
        value: t.id,
      })),
    [alertTypes]
  );

  const handleSave = useCallback(() => {
    if (!locFormattedAddress || !locFormattedAddress.trim()) {
      (window as any).App?.showPopupNotification?.('Location required.', 'error');
      return;
    }
    if (callAlertTypeId === null) {
      (window as any).App?.showPopupNotification?.('Type required.', 'error');
      return;
    }
    if (!message || !message.trim()) {
      (window as any).App?.showPopupNotification?.('Message required.', 'error');
      return;
    }

    onSave({
      id: mode === 'edit' ? callAlert?.id : undefined,
      locFormattedAddress: locFormattedAddress.trim(),
      callAlertTypeId,
      message: message.trim(),
      alertStartTime,
      alertEndTime,
      originalLocation,
      locationChanged: locFormattedAddress.trim() !== originalLocation,
    });
  }, [locFormattedAddress, callAlertTypeId, message, alertStartTime, alertEndTime, originalLocation, mode, callAlert, onSave]);

  return (
    <BaseFormModal
      open={open}
      title={modalTitle}
      theme={theme}
      dialogClass="kstc-dialog--form kstc-dialog--wide"
      saveText={saving ? 'Saving...' : saveButtonText}
      saveDisabled={saving}
      busy={saving}
      onSave={handleSave}
      onCancel={onCancel}
    >
      <div className="kstc-form-group">
        <label className="kstc-label">Location</label>
        <input
          ref={inputRef}
          type="text"
          className="kstc-input kst-location-input"
          value={locFormattedAddress}
          onChange={(e) => setLocFormattedAddress(e.target.value)}
          placeholder="Enter location..."
        />
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Type</label>
        <Dropdownlist
          value={callAlertTypeId}
          items={alertTypeItems}
          placeholder="Select type..."
          sortItems={false}
          searchable={false}
          onChange={(val) => setCallAlertTypeId(val as number | null)}
        />
      </div>

      <div className="kstc-form-row">
        <div className="kstc-form-group kstc-form-group--half">
          <label className="kstc-label">Start Time (optional)</label>
          <DateTimePicker
            value={alertStartTime}
            type="datetime"
            format="MM/DD/YYYY hh:mm A"
            clearable={true}
            appendToBody={true}
            onChange={setAlertStartTime}
          />
        </div>
        <div className="kstc-form-group kstc-form-group--half">
          <label className="kstc-label">End Time (optional)</label>
          <DateTimePicker
            value={alertEndTime}
            type="datetime"
            format="MM/DD/YYYY hh:mm A"
            clearable={true}
            appendToBody={true}
            onChange={setAlertEndTime}
          />
        </div>
      </div>

      <div className="kstc-form-group">
        <label className="kstc-label">Message</label>
        <textarea
          className="kstc-textarea"
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          placeholder="Enter message..."
          rows={4}
        />
      </div>
    </BaseFormModal>
  );
}

(window as any).AddEditCallAlertModalReact = AddEditCallAlertModal;
